import React, { useEffect, useRef, useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { differenceInDays } from 'date-fns';

import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import type { DismissFn } from '@atlaskit/flag';
import { FlagsProvider, useFlags } from '@atlaskit/flag';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	ONBOARDING_EXPERIENCES,
	NTH_USER_ONBOARDING_FLAG_VIEWED,
	NTH_USER_ONBOARDING_COMPLETED_TIMESTAMP,
	IMPROVED_GET_STARTED_NTH_USER_PATH,
	NTH_USER_ONBOARDING_PAGE_VIEWED,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	useSetOnboardingState,
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';

const messages = defineMessages({
	nthUserOnboardingFlagTitle: {
		id: 'experiment-nth-user-getting-started-page.nth-user-onboarding.flag.title',
		defaultMessage: 'Just in case you missed it',
		description:
			'Title of a flag that encourages users to visit the getting started page if not already viewed',
	},
	nthUserOnboardingFlagContent: {
		id: 'experiment-nth-user-getting-started-page.nth-user-onboarding.flag.content',
		defaultMessage: "We've got some handy tips and tricks to help you get started in Confluence.",
		description:
			'The text is shown as the body content inside a notification flag displayed to nth users during onboarding. It is the start of a sentence informing the user of an action taken on their behalf.',
	},
	nthUserOnboardingFlagAction: {
		id: 'experiment-nth-user-getting-started-page.nth-user-onboarding.flag.action',
		defaultMessage: 'Check it out',
		description:
			'The text is shown as a call-to-action link or button inside the nth user onboarding notification flag. When clicked, it navigates the user to the relevant onboarding content.',
	},
	nthUserOnboardingFlagLabel: {
		id: 'experiment-nth-user-getting-started-page.nth-user-onboarding.flag.label',
		defaultMessage: 'Lightbulb',
		description:
			'The text is used as the accessible label for the lightbulb icon displayed in the nth user onboarding notification flag. It describes the icon to screen reader users.',
	},
});

export const extractContentIdFromUrl = (url: string): string | null => {
	// Pattern: /spaces/{spaceKey}/pages/{contentId}/{page-title}
	const match = url.match(/\/spaces\/[^/]+\/pages\/(\d+)/);
	return match ? match[1] : null;
};

const DAYS_TO_WAIT_AFTER_ONBOARDING_COMPLETION = 1;
const useNthUserOnboardingFlag = () => {
	const { showFlag } = useFlags();
	const { setOnboardingState } = useSetOnboardingState();
	const { push } = useRouteActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const flagId = 'NTH_USER_ONBOARDING_FLAG';
	const dismissFlag = useRef<DismissFn | null>(null);
	const showNthUserOnboardingFlag = useCallback(
		(nthUserGSPage: string) => {
			void setOnboardingState({
				key: ONBOARDING_EXPERIENCES.NTH_USER_ONBOARDING_FLAG_VIEWED,
				value: 'true',
			});
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'viewNthUserOnboardingFlag',
				},
			}).fire();
			const handleActionClick = () => {
				if (dismissFlag.current) {
					dismissFlag.current();
				}
				push(nthUserGSPage);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'flag',
						actionSubjectId: flagId,
						source: 'homepage',
					},
				}).fire();
			};

			dismissFlag.current = showFlag({
				id: flagId,
				testId: 'nth-user-onboarding-flag',
				icon: <LightbulbIcon label={messages.nthUserOnboardingFlagLabel.defaultMessage} />,
				title: <FormattedMessage {...messages.nthUserOnboardingFlagTitle} />,
				description: <FormattedMessage {...messages.nthUserOnboardingFlagContent} />,
				actions: [
					{
						content: <FormattedMessage {...messages.nthUserOnboardingFlagAction} />,
						onClick: handleActionClick,
					},
				],
				onDismissed: () => {
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'dismissed',
							actionSubject: 'flag',
							actionSubjectId: flagId,
							source: 'homepage',
						},
					}).fire();
				},
			});
		},
		[showFlag, setOnboardingState, push, flagId, createAnalyticsEvent],
	);

	return { showNthUserOnboardingFlag };
};

const NthUserOnboardingFlagInner = () => {
	const { cloudId, userId } = useSessionData();
	const {
		data: onboardingStateData,
		error: onboardingStateError,
		loading: onboardingStateLoading,
	} = useGetOnboardingState([
		ONBOARDING_EXPERIENCES.NTH_USER_ONBOARDING_FLAG_VIEWED,
		ONBOARDING_EXPERIENCES.NTH_USER_ONBOARDING_COMPLETED_TIMESTAMP,
		ONBOARDING_EXPERIENCES.NTH_USER_ONBOARDING_PAGE_VIEWED,
		ONBOARDING_EXPERIENCES.IMPROVED_GET_STARTED_NTH_USER_PATH,
	]);

	const deserializedState = deserializeState(onboardingStateData);
	const { showNthUserOnboardingFlag } = useNthUserOnboardingFlag();

	const timestamp = deserializedState[NTH_USER_ONBOARDING_COMPLETED_TIMESTAMP];
	const nthUserFlagViewed = deserializedState[NTH_USER_ONBOARDING_FLAG_VIEWED];
	const nthUserGSPage = deserializedState[IMPROVED_GET_STARTED_NTH_USER_PATH];
	const nthUserOnboardingPageViewed = deserializedState[NTH_USER_ONBOARDING_PAGE_VIEWED];

	// Show flag once when all conditions are met
	useEffect(() => {
		if (!cloudId || !userId) {
			return;
		}

		if (
			onboardingStateLoading ||
			onboardingStateError ||
			!timestamp ||
			nthUserFlagViewed ||
			nthUserOnboardingPageViewed
		) {
			return;
		}

		const daysSinceCompletion = differenceInDays(new Date(), new Date(Number(timestamp)));

		if (daysSinceCompletion < DAYS_TO_WAIT_AFTER_ONBOARDING_COMPLETION || !nthUserGSPage) {
			return;
		}

		showNthUserOnboardingFlag(nthUserGSPage);
	}, [
		onboardingStateLoading,
		onboardingStateError,
		nthUserFlagViewed,
		nthUserOnboardingPageViewed,
		nthUserGSPage,
		deserializedState,
		showNthUserOnboardingFlag,
		timestamp,
		cloudId,
		userId,
	]);

	return null;
};
export const NthUserOnboardingFlag = () => {
	return (
		<FlagsProvider>
			<NthUserOnboardingFlagInner />
		</FlagsProvider>
	);
};
